import React, { useState, useEffect } from 'react';
import { Users, Loader2, AlertCircle, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';

interface CrossRefResult {
  source: 'crm' | 'turnusy' | 'szkolenia';
  id: number;
  first_name: string;
  last_name: string;
  type: string;
  status: string;
  created_at: string;
  extra: string | null;
}

interface Props {
  applicationId: number;
  email?: string | null;
  pesel?: string | null;
}

function getToken(): string {
  return localStorage.getItem('grant_crm_token') || localStorage.getItem('crm_token') || '';
}

const SOURCE_LABELS: Record<string, { label: string; color: string; route?: (r: CrossRefResult) => string }> = {
  crm: {
    label: 'CRM',
    color: 'bg-blue-100 text-blue-800',
    route: (r) => `/crm/${r.id}`,
  },
  turnusy: {
    label: 'Turnusy',
    color: 'bg-orange-100 text-orange-800',
  },
  szkolenia: {
    label: 'Szkolenia',
    color: 'bg-purple-100 text-purple-800',
  },
};

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('pl-PL', { year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    return dateStr;
  }
}

export const ClientCrossReferencePanel: React.FC<Props> = ({ applicationId, email, pesel }) => {
  const [results, setResults] = useState<CrossRefResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!email && !pesel) return;
    setLoading(true);
    fetch(`/api/crm/applications/${applicationId}/client-cross-reference`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.json())
      .then(data => {
        setResults(data.results || []);
        // Auto-expand if there are matches
        if ((data.results || []).length > 0) setExpanded(true);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [applicationId, email, pesel]);

  // Count per source
  const counts = results.reduce<Record<string, number>>((acc, r) => {
    acc[r.source] = (acc[r.source] || 0) + 1;
    return acc;
  }, {});

  const totalCount = results.length;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header — collapsible */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Users size={16} className="text-gray-500" />
          <span className="font-semibold text-gray-700 text-sm">Klient w innych systemach</span>
          {loading ? (
            <Loader2 size={12} className="animate-spin text-gray-400" />
          ) : totalCount > 0 ? (
            <span className="px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {totalCount}
            </span>
          ) : (
            <span className="text-xs text-gray-400">brak powiązań</span>
          )}
        </div>
        {expanded ? (
          <ChevronUp size={14} className="text-gray-400" />
        ) : (
          <ChevronDown size={14} className="text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="p-3">
          {error ? (
            <div className="flex items-center gap-2 text-sm text-red-600">
              <AlertCircle size={13} />
              {error}
            </div>
          ) : loading ? (
            <div className="flex items-center gap-2 py-3 text-gray-400 text-sm">
              <Loader2 size={14} className="animate-spin" />
              Wyszukiwanie...
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-gray-400 py-3 text-center">
              Klient nie figuruje w innych systemach
            </p>
          ) : (
            <>
              {/* Summary chips */}
              <div className="flex flex-wrap gap-2 mb-3">
                {Object.entries(SOURCE_LABELS).map(([key, cfg]) => {
                  const count = counts[key] || 0;
                  return (
                    <span
                      key={key}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        count > 0 ? cfg.color : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {cfg.label}: {count}
                    </span>
                  );
                })}
              </div>

              {/* Results list */}
              <div className="space-y-1.5">
                {results.map((r, i) => {
                  const cfg = SOURCE_LABELS[r.source] || { label: r.source, color: 'bg-gray-100 text-gray-700' };
                  const route = cfg.route?.(r);
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-2 px-2 py-1.5 bg-gray-50 rounded text-sm"
                    >
                      <span className={`mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">
                          {r.first_name} {r.last_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {r.type}
                          {r.status && r.status !== 'n/a' && (
                            <span className="ml-1 text-gray-400">· {r.status}</span>
                          )}
                          <span className="ml-1 text-gray-400">· {formatDate(r.created_at)}</span>
                        </div>
                      </div>
                      {route && (
                        <a
                          href={route}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 text-blue-500 hover:text-blue-700 mt-0.5"
                          title="Otwórz wniosek"
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
